/**
 * Click Log — raw click log with filters + CSV export (FS-827).
 *
 * UX goal: forensic tool. An operator lands here to investigate a fraud
 * report, reconcile a payout, or debug a missing conversion. The page
 * therefore optimises for:
 *
 *  1. Fast filter narrowing (date-range, campaign, affiliate, country, UA,
 *     click-id).
 *  2. Dense, monospace table — every row must be skim-able.
 *  3. One-shot CSV export of the current filter for spreadsheet analysis,
 *     with a warning banner when the selection exceeds 10k rows.
 *
 * Brand: emerald/sky gradient headline, zinc borders, light theme only.
 * No country flag emojis — ISO code lives in a zinc-monospace badge.
 *
 * The backend is `/api/v1/click-level-detail-reports` (shipped).
 *
 *   GET  /clicks           → filtered + cursor-paginated list.
 *   POST /export-csv       → streamed CSV (capped at 10k rows server-side).
 *
 * The full rendering lives in one component — breaking sub-components into
 * sibling files would widen the owned-file surface past what the contract
 * permits.
 */

import { useMemo, useRef, useState } from "react";
import {
	flexRender,
	getCoreRowModel,
	useReactTable,
	type ColumnDef,
} from "@tanstack/react-table";
import {
	ALLOWED_STATUSES,
	countryBadgeClass,
	EXPORT_WARN_THRESHOLD,
	formatUtcTimestamp,
	last24hSince,
	nowSec,
	statusBadgeClass,
	truncateEnd,
	truncateMiddle,
	type ClickLogFilters,
	type ClickLogRow,
	type ClickStatus,
} from "./api";
import {
	useClickLogCount,
	useClickLogList,
	useExportClicksCsv,
} from "./hooks";

// ---------------------------------------------------------------------------
// Style tokens — mirror the DESIGN.md spec; no shared tokens file to avoid
// widening the owned-file surface.
// ---------------------------------------------------------------------------

const CARD =
	"rounded-lg border border-zinc-200 bg-white shadow-sm";
const LABEL = "block text-xs font-medium text-zinc-500 uppercase tracking-wider";
const INPUT =
	"w-full rounded-md border border-zinc-200 bg-white px-3 py-2 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20";
const BTN_PRIMARY =
	"rounded-md bg-emerald-500 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-emerald-600 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_SECONDARY =
	"rounded-md border border-zinc-200 bg-white px-4 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60";
const BADGE =
	"inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium";
const BADGE_ISO =
	"inline-flex items-center px-1.5 py-0.5 rounded text-xs";

// Device-type dropdown options — must match the backend enum.
const DEVICE_TYPES: { value: string; label: string }[] = [
	{ value: "", label: "Any device" },
	{ value: "desktop", label: "Desktop" },
	{ value: "mobile", label: "Mobile" },
	{ value: "tablet", label: "Tablet" },
	{ value: "tv", label: "TV" },
	{ value: "bot", label: "Bot" },
	{ value: "other", label: "Other" },
];

// ---------------------------------------------------------------------------
// Filter draft — what lives in the filter bar inputs before the user hits
// "Apply". The applied filter is what the list + count queries are keyed on.
// ---------------------------------------------------------------------------

interface FilterDraft {
	sinceLocal: string; // yyyy-mm-ddThh:mm (local datetime-local input value)
	untilLocal: string;
	campaign_id: string;
	affiliate_id: string;
	click_id: string;
	country: string;
	device_type: string;
	status: ClickStatus | "";
}

function localDatetimeFromSec(sec: number): string {
	const d = new Date(sec * 1000);
	const pad = (n: number) => String(n).padStart(2, "0");
	return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function secFromLocalDatetime(v: string): number | null {
	if (!v) return null;
	const t = Date.parse(v);
	if (!Number.isFinite(t)) return null;
	return Math.floor(t / 1000);
}

function initialDraft(): FilterDraft {
	return {
		sinceLocal: localDatetimeFromSec(last24hSince()),
		untilLocal: localDatetimeFromSec(nowSec()),
		campaign_id: "",
		affiliate_id: "",
		click_id: "",
		country: "",
		device_type: "",
		status: "",
	};
}

function draftToFilters(d: FilterDraft): ClickLogFilters {
	return {
		since: secFromLocalDatetime(d.sinceLocal),
		until: secFromLocalDatetime(d.untilLocal),
		campaign_id: d.campaign_id.trim() || null,
		affiliate_id: d.affiliate_id.trim() || null,
		click_id: d.click_id.trim() || null,
		country: d.country.trim() || null,
		device_type: d.device_type || null,
		status: d.status ? [d.status] : null,
	};
}

// ---------------------------------------------------------------------------
// Copy-to-clipboard helper — best-effort, silent on failure.
// ---------------------------------------------------------------------------

async function copyToClipboard(text: string): Promise<boolean> {
	try {
		if (typeof navigator !== "undefined" && navigator.clipboard) {
			await navigator.clipboard.writeText(text);
			return true;
		}
	} catch {
		/* fall through */
	}
	return false;
}

// ---------------------------------------------------------------------------
// Table columns — dense, monospace where helpful.
// ---------------------------------------------------------------------------

function useColumns(
	copiedId: string | null,
	onCopy: (id: string) => void,
): ColumnDef<ClickLogRow>[] {
	return useMemo<ColumnDef<ClickLogRow>[]>(
		() => [
			{
				id: "ts",
				header: "Timestamp (UTC)",
				accessorKey: "ts",
				cell: (ctx) => (
					<span className="font-mono text-xs text-zinc-700">
						{formatUtcTimestamp(ctx.getValue() as number)}
					</span>
				),
			},
			{
				id: "click_id",
				header: "Click ID",
				accessorKey: "click_id",
				cell: (ctx) => {
					const id = ctx.getValue() as string;
					const copied = copiedId === id;
					return (
						<div className="flex items-center gap-2">
							<span
								className="font-mono text-xs text-zinc-900"
								title={id}
							>
								{truncateMiddle(id, 24)}
							</span>
							<button
								type="button"
								className="rounded border border-zinc-200 px-1.5 py-0.5 text-[10px] font-medium text-zinc-600 hover:bg-zinc-50"
								onClick={() => onCopy(id)}
								aria-label={`Copy click ID ${id}`}
								data-testid={`copy-click-id-${id}`}
							>
								{copied ? "Copied" : "Copy"}
							</button>
						</div>
					);
				},
			},
			{
				id: "ip",
				header: "IP",
				accessorKey: "ip",
				cell: (ctx) => {
					const ip = ctx.getValue() as string | null;
					if (!ip) return <span className="text-zinc-400">—</span>;
					return (
						<span
							className="font-mono text-xs text-zinc-700"
							title={ip}
						>
							{truncateEnd(ip, 18)}
						</span>
					);
				},
			},
			{
				id: "country",
				header: "Country",
				accessorKey: "country",
				cell: (ctx) => {
					const c = ctx.getValue() as string | null;
					if (!c) return <span className="text-zinc-400">—</span>;
					return (
						<span className={`${BADGE_ISO} ${countryBadgeClass()}`}>
							{c.toUpperCase()}
						</span>
					);
				},
			},
			{
				id: "ua",
				header: "UA",
				accessorKey: "ua",
				cell: (ctx) => {
					const ua = ctx.getValue() as string | null;
					if (!ua) return <span className="text-zinc-400">—</span>;
					return (
						<span
							className="text-xs text-zinc-700"
							title={ua}
						>
							{truncateEnd(ua, 36)}
						</span>
					);
				},
			},
			{
				id: "campaign_id",
				header: "Campaign",
				accessorKey: "campaign_id",
				cell: (ctx) => {
					const v = ctx.getValue() as string | null;
					return v ? (
						<span className="font-mono text-xs text-zinc-700">{truncateEnd(v, 20)}</span>
					) : (
						<span className="text-zinc-400">—</span>
					);
				},
			},
			{
				id: "affiliate_id",
				header: "Affiliate",
				accessorKey: "affiliate_id",
				cell: (ctx) => {
					const v = ctx.getValue() as string | null;
					return v ? (
						<span className="font-mono text-xs text-zinc-700">{truncateEnd(v, 20)}</span>
					) : (
						<span className="text-zinc-400">—</span>
					);
				},
			},
			{
				id: "status",
				header: "Landed",
				accessorKey: "status",
				cell: (ctx) => {
					const s = ctx.getValue() as ClickStatus;
					return (
						<span className={`${BADGE} ${statusBadgeClass(s)}`}>
							{s}
						</span>
					);
				},
			},
			{
				id: "converted",
				header: "Converted",
				accessorKey: "attributed_conversion_id",
				cell: (ctx) => {
					const v = ctx.getValue() as string | null;
					if (!v) return <span className="text-zinc-400">—</span>;
					return (
						<span
							className="font-mono text-xs text-emerald-700"
							title={v}
						>
							{truncateMiddle(v, 18)}
						</span>
					);
				},
			},
		],
		[copiedId, onCopy],
	);
}

// ---------------------------------------------------------------------------
// ClickLogReportPage — the full forensic view.
// ---------------------------------------------------------------------------

export function ClickLogReportPage() {
	const [draft, setDraft] = useState<FilterDraft>(initialDraft);
	const [applied, setApplied] = useState<ClickLogFilters>(() =>
		draftToFilters(initialDraft()),
	);
	const [copiedId, setCopiedId] = useState<string | null>(null);
	const copyTimer = useRef<number | null>(null);

	const listQuery = useClickLogList(applied);
	const countQuery = useClickLogCount(applied, true);
	const exportMutation = useExportClicksCsv();

	// Flatten paginated rows into a single list the table renders.
	const rows = useMemo<ClickLogRow[]>(() => {
		if (!listQuery.data) return [];
		return listQuery.data.pages.flatMap((p) => p.data);
	}, [listQuery.data]);

	const handleCopy = (id: string) => {
		void copyToClipboard(id);
		setCopiedId(id);
		if (copyTimer.current != null) window.clearTimeout(copyTimer.current);
		copyTimer.current = window.setTimeout(() => {
			setCopiedId(null);
			copyTimer.current = null;
		}, 1500);
	};

	const columns = useColumns(copiedId, handleCopy);

	const table = useReactTable({
		data: rows,
		columns,
		getCoreRowModel: getCoreRowModel(),
	});

	const totalMatched = countQuery.data ?? null;
	const showExportWarning =
		totalMatched != null && totalMatched > EXPORT_WARN_THRESHOLD;

	const handleApply = (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		setApplied(draftToFilters(draft));
	};

	const handleReset = () => {
		const fresh = initialDraft();
		setDraft(fresh);
		setApplied(draftToFilters(fresh));
	};

	const handleExport = async () => {
		try {
			const { blob, filename } = await exportMutation.mutateAsync({
				filters: applied,
			});
			// Wire to browser download via an in-memory object URL. We stay
			// compatible with the `<a href={blobUrl} download>` pattern.
			const url = URL.createObjectURL(blob);
			const a = document.createElement("a");
			a.href = url;
			a.download = filename;
			document.body.appendChild(a);
			a.click();
			a.remove();
			// Revoke on the next tick so Safari has a chance to grab the blob.
			window.setTimeout(() => URL.revokeObjectURL(url), 1000);
		} catch {
			/* mutation.error surfaces to the UI banner */
		}
	};

	return (
		<div
			className="mx-auto flex w-full max-w-7xl flex-col gap-6 px-6 py-6"
			data-testid="click-log-report-page"
		>
			{/* Header */}
			<header className="flex items-start justify-between gap-4">
				<div className="flex flex-col gap-1">
					<h1 className="bg-gradient-to-r from-emerald-500 to-sky-500 bg-clip-text text-2xl font-semibold text-transparent">
						Click Log
					</h1>
					<p className="text-sm text-zinc-500">
						Raw click stream for forensic drill-down — filter by time,
						campaign, affiliate, country, or click ID, then export the
						current selection as CSV.
					</p>
				</div>
				<div className="flex items-center gap-3">
					{totalMatched != null && (
						<span
							className="text-xs text-zinc-500"
							data-testid="click-log-total"
						>
							{totalMatched.toLocaleString()} matching
						</span>
					)}
					<button
						type="button"
						className={BTN_PRIMARY}
						onClick={handleExport}
						disabled={exportMutation.isPending}
						data-testid="click-log-export-btn"
					>
						{exportMutation.isPending ? "Exporting…" : "Export CSV"}
					</button>
				</div>
			</header>

			{/* Export warning banner */}
			{showExportWarning && (
				<div
					className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800"
					role="alert"
					data-testid="click-log-export-warning"
				>
					Export will include {totalMatched?.toLocaleString()} rows — may
					take a minute. The server caps exports at 10,000 rows per request;
					narrow the date range to capture the full set.
				</div>
			)}

			{exportMutation.isError && (
				<div
					className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
					role="alert"
					data-testid="click-log-export-error"
				>
					Export failed: {exportMutation.error.message}
				</div>
			)}

			{/* Filter bar */}
			<form
				onSubmit={handleApply}
				className={`${CARD} p-4`}
				aria-label="Click log filters"
				data-testid="click-log-filter-bar"
			>
				<div className="grid grid-cols-1 gap-4 md:grid-cols-3 lg:grid-cols-4">
					<div>
						<label htmlFor="cl-since" className={LABEL}>
							From (local)
						</label>
						<input
							id="cl-since"
							type="datetime-local"
							className={INPUT}
							value={draft.sinceLocal}
							onChange={(e) =>
								setDraft((d) => ({ ...d, sinceLocal: e.target.value }))
							}
						/>
					</div>
					<div>
						<label htmlFor="cl-until" className={LABEL}>
							Until (local)
						</label>
						<input
							id="cl-until"
							type="datetime-local"
							className={INPUT}
							value={draft.untilLocal}
							onChange={(e) =>
								setDraft((d) => ({ ...d, untilLocal: e.target.value }))
							}
						/>
					</div>
					<div>
						<label htmlFor="cl-campaign" className={LABEL}>
							Campaign
						</label>
						<input
							id="cl-campaign"
							type="text"
							placeholder="campaign_id"
							className={INPUT}
							value={draft.campaign_id}
							onChange={(e) =>
								setDraft((d) => ({ ...d, campaign_id: e.target.value }))
							}
							autoComplete="off"
						/>
					</div>
					<div>
						<label htmlFor="cl-affiliate" className={LABEL}>
							Affiliate
						</label>
						<input
							id="cl-affiliate"
							type="text"
							placeholder="affiliate_id"
							className={INPUT}
							value={draft.affiliate_id}
							onChange={(e) =>
								setDraft((d) => ({ ...d, affiliate_id: e.target.value }))
							}
							autoComplete="off"
						/>
					</div>
					<div>
						<label htmlFor="cl-click-id" className={LABEL}>
							Click ID (exact)
						</label>
						<input
							id="cl-click-id"
							type="text"
							placeholder="clk_abc123…"
							className={INPUT}
							value={draft.click_id}
							onChange={(e) =>
								setDraft((d) => ({ ...d, click_id: e.target.value }))
							}
							autoComplete="off"
							spellCheck={false}
						/>
					</div>
					<div>
						<label htmlFor="cl-country" className={LABEL}>
							Country (ISO)
						</label>
						<input
							id="cl-country"
							type="text"
							placeholder="US, DE, …"
							className={INPUT}
							value={draft.country}
							onChange={(e) =>
								setDraft((d) => ({ ...d, country: e.target.value }))
							}
							autoComplete="off"
							maxLength={8}
						/>
					</div>
					<div>
						<label htmlFor="cl-device" className={LABEL}>
							Device
						</label>
						<select
							id="cl-device"
							className={INPUT}
							value={draft.device_type}
							onChange={(e) =>
								setDraft((d) => ({ ...d, device_type: e.target.value }))
							}
						>
							{DEVICE_TYPES.map((o) => (
								<option key={o.value} value={o.value}>
									{o.label}
								</option>
							))}
						</select>
					</div>
					<div>
						<label htmlFor="cl-status" className={LABEL}>
							Status
						</label>
						<select
							id="cl-status"
							className={INPUT}
							value={draft.status}
							onChange={(e) =>
								setDraft((d) => ({
									...d,
									status: e.target.value as ClickStatus | "",
								}))
							}
						>
							<option value="">Any status</option>
							{ALLOWED_STATUSES.map((s) => (
								<option key={s} value={s}>
									{s}
								</option>
							))}
						</select>
					</div>
				</div>
				<div className="mt-4 flex items-center justify-end gap-2">
					<button
						type="button"
						className={BTN_SECONDARY}
						onClick={handleReset}
						data-testid="click-log-reset-btn"
					>
						Reset
					</button>
					<button
						type="submit"
						className={BTN_PRIMARY}
						data-testid="click-log-apply-btn"
					>
						Apply
					</button>
				</div>
			</form>

			{/* Table */}
			<section className={CARD} aria-label="Click log results">
				{listQuery.isLoading ? (
					<div
						className="px-6 py-8 text-sm text-zinc-500"
						data-testid="click-log-loading"
					>
						Loading clicks…
					</div>
				) : listQuery.isError ? (
					<div
						className="px-6 py-8 text-sm text-red-600"
						role="alert"
						data-testid="click-log-error"
					>
						Failed to load clicks: {listQuery.error.message}
					</div>
				) : rows.length === 0 ? (
					<div
						className="px-6 py-8 text-sm text-zinc-500"
						data-testid="click-log-empty"
					>
						No clicks match the current filters. Widen the date range or
						clear a filter.
					</div>
				) : (
					<div className="overflow-x-auto">
						<table
							className="w-full border-collapse text-sm"
							data-testid="click-log-table"
						>
							<thead className="bg-zinc-50 text-xs uppercase tracking-wider text-zinc-500">
								{table.getHeaderGroups().map((hg) => (
									<tr key={hg.id}>
										{hg.headers.map((h) => (
											<th
												key={h.id}
												className="px-4 py-3 text-left font-medium"
											>
												{flexRender(
													h.column.columnDef.header,
													h.getContext(),
												)}
											</th>
										))}
									</tr>
								))}
							</thead>
							<tbody>
								{table.getRowModel().rows.map((row) => (
									<tr
										key={row.id}
										className="transition-colors hover:bg-emerald-50/40"
										data-testid={`click-log-row-${row.original.click_id}`}
									>
										{row.getVisibleCells().map((cell) => (
											<td
												key={cell.id}
												className="border-t border-zinc-200 px-4 py-3 align-middle"
											>
												{flexRender(
													cell.column.columnDef.cell,
													cell.getContext(),
												)}
											</td>
										))}
									</tr>
								))}
							</tbody>
						</table>
					</div>
				)}
			</section>

			{/* Pagination — cursor-based "Load more". */}
			{rows.length > 0 && (
				<div className="flex items-center justify-between">
					<span className="text-xs text-zinc-500">
						Showing {rows.length.toLocaleString()} of{" "}
						{totalMatched != null ? totalMatched.toLocaleString() : "—"}
					</span>
					<button
						type="button"
						className={BTN_SECONDARY}
						onClick={() => listQuery.fetchNextPage()}
						disabled={
							!listQuery.hasNextPage || listQuery.isFetchingNextPage
						}
						data-testid="click-log-load-more"
					>
						{listQuery.isFetchingNextPage
							? "Loading…"
							: listQuery.hasNextPage
								? "Load more"
								: "End of results"}
					</button>
				</div>
			)}
		</div>
	);
}

export default ClickLogReportPage;
